import * as React from "react";
import { ClientBankAccount } from "../../models.ts";

interface Props {
    bankAccounts: ClientBankAccount[];
    size?: number;
    max?: number;
}

interface StackedLogo {
    logo_url: string;
    is_expired: boolean;
    expires_soon: boolean;
}

function toStackedLogos(bankAccounts: ClientBankAccount[]): StackedLogo[] {
    const byUrl = new Map<string, StackedLogo>();
    for (const account of bankAccounts) {
        if (!account.logo_url) {
            continue;
        }
        const existing = byUrl.get(account.logo_url);
        byUrl.set(account.logo_url, {
            logo_url: account.logo_url,
            is_expired: account.is_expired || (existing?.is_expired ?? false),
            expires_soon: account.expires_soon || (existing?.expires_soon ?? false),
        });
    }
    return [...byUrl.values()];
}

function consentSeverity(logo: StackedLogo): number {
    if (logo.is_expired) {
        return 2;
    }
    if (logo.expires_soon) {
        return 1;
    }
    return 0;
}

function logoStateClasses(logo: StackedLogo): string {
    if (logo.is_expired) {
        return "ring-red-500 opacity-100";
    }
    if (logo.expires_soon) {
        return "ring-amber-500 opacity-100";
    }
    return "ring-white grayscale-[.55] opacity-90 group-hover/row:grayscale-0 group-hover/row:opacity-100";
}

export function BankLogoStack({ bankAccounts, size = 28, max = 5 }: Props) {
    const logos = toStackedLogos(bankAccounts);

    if (logos.length === 0) {
        return <span className="text-gray-300 text-sm">–</span>;
    }

    const ordered = [...logos].sort((left, right) => consentSeverity(right) - consentSeverity(left));
    const visible = ordered.slice(0, max);
    const overflow = logos.length - visible.length;
    const overlap = Math.round(size * 0.6);
    const spread = Math.round(size * 0.4);

    return (
        <div className="flex items-center">
            {visible.map((logo, index) => (
                <div
                    key={`${logo.logo_url}-${index}`}
                    className={`flex items-center justify-center overflow-hidden rounded-full bg-white shadow-sm ring-2 ${logoStateClasses(logo)} transition-all duration-300 ease-out group-hover/row:[transform:translateX(var(--tx))]`}
                    style={{
                        width: size,
                        height: size,
                        marginLeft: index === 0 ? 0 : -overlap,
                        zIndex: index,
                        transitionDelay: `${index * 40}ms`,
                        ["--tx" as string]: `${index * spread}px`,
                    } as React.CSSProperties}
                >
                    <img
                        src={logo.logo_url}
                        alt=""
                        className="h-full w-full object-contain"
                        onError={(e) => {
                            e.currentTarget.style.display = "none";
                        }}
                    />
                </div>
            ))}
            {overflow > 0 && (
                <div
                    className="flex items-center justify-center rounded-full bg-gray-100 font-medium text-gray-500 ring-2 ring-white transition-all duration-300 ease-out group-hover/row:text-gray-600 group-hover/row:[transform:translateX(var(--tx))]"
                    style={{
                        width: size,
                        height: size,
                        marginLeft: -overlap,
                        zIndex: max,
                        fontSize: size * 0.36,
                        transitionDelay: `${visible.length * 40}ms`,
                        ["--tx" as string]: `${visible.length * spread}px`,
                    } as React.CSSProperties}
                >
                    +{overflow}
                </div>
            )}
        </div>
    );
}
